import React, { useState, useRef, useEffect } from 'react';
import { parseTimeToSeconds, formatTimeFromSeconds } from './videoResourceService';
import './EnhancedVideoPlayer.css';

/**
 * Enhanced video player component for result cards
 */
const EnhancedVideoPlayer = ({ videoPath, startTime, endTime, onClose, onGoDetail }) => {
  const videoRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [displayTime, setDisplayTime] = useState('0:00');
  const [isClipMode, setIsClipMode] = useState(true);
  
  // Flags to prevent duplicate initialization and event listeners
  const initializedRef = useRef(false);
  const isPlayingRef = useRef(false);
  
  // Convert start/end times to seconds
  const startTimeSecondsRaw = parseTimeToSeconds(startTime) || 0;
  
  // Handle end time - if not provided, default to playing 30 seconds
  const endTimeSecondsRaw = endTime ? parseTimeToSeconds(endTime) : startTimeSecondsRaw + 10;
  
  // Ensure always enough playback time (at least 10 seconds)
  let adjustedStart = startTimeSecondsRaw;
  let adjustedEnd = Math.max(startTimeSecondsRaw + 10, endTimeSecondsRaw);
  
  // Log time settings
  console.log("Video time settings:", {
    originalStart: startTime,
    originalEnd: endTime,
    startSeconds: startTimeSecondsRaw,
    endSeconds: endTimeSecondsRaw,
    adjustedStart: adjustedStart,
    adjustedEnd: adjustedEnd,
    clipMode: isClipMode
  });
  
  // Process video path
  const getVideoUrl = (path) => {
    if (!path) return null;
    
    if (path.startsWith('http')) {
      return path;
    }
    
    // Ensure path starts with a slash
    const normalizedPath = path.startsWith('/') ? path : `/${path}`;
    
    // Check if we need to add low resolution for day videos
    if (normalizedPath.includes('/day/videos/')) {
      return normalizedPath.replace('/day/videos/', '/day/videos-low/');
    }
    
    return normalizedPath;
  };
  
  const videoUrl = getVideoUrl(videoPath);
  
  // Initialize video player - runs only once
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !videoUrl || initializedRef.current) return;
    
    console.log("Initializing video player:", videoUrl);
    initializedRef.current = true;
    
    // Set initial time
    video.currentTime = adjustedStart;
    
    // Video load handler
    const handleCanPlay = () => {
      console.log("Video can play");
      setIsLoading(false);
    };
    
    // Video error handler
    const handleError = (e) => {
      console.error("Video loading error:", e);
      setError(`Error loading video: ${e.target.error?.message || 'Unknown error'}`);
      setIsLoading(false);
      isPlayingRef.current = false;
    };
    
    // Update display time
    const handleTimeUpdate = () => {
      setDisplayTime(formatTimeFromSeconds(video.currentTime));
      
      // Only check for end time if in clip mode
      if (isClipMode && typeof adjustedEnd === 'number' && video.currentTime >= adjustedEnd) {
        console.log("Reached end time, pausing video");
        console.log(`Current time: ${video.currentTime}s, Set end time: ${adjustedEnd}s`);
        video.pause();
        isPlayingRef.current = false;
      }
    };
    
    // Add event listeners
    video.addEventListener('canplay', handleCanPlay);
    video.addEventListener('error', handleError);
    video.addEventListener('timeupdate', handleTimeUpdate);
    
    // Cleanup event listeners
    return () => {
      video.removeEventListener('canplay', handleCanPlay);
      video.removeEventListener('error', handleError);
      video.removeEventListener('timeupdate', handleTimeUpdate);
      initializedRef.current = false;
      isPlayingRef.current = false;
    };
  }, [videoUrl, adjustedStart, adjustedEnd, isClipMode]); 
  
  // Play functionality
  const handlePlay = () => {
    const video = videoRef.current;
    if (!video) return;
    
    if (video.paused) {
      // If video is paused, check if it reached the end in clip mode
      if (isClipMode && video.currentTime >= adjustedEnd) {
        // If at end, reset to start time
        video.currentTime = adjustedStart;
      }
      
      // Start playing
      isPlayingRef.current = true;
      video.play()
        .then(() => console.log("Video playing successfully"))
        .catch(err => {
          console.error("Play failed:", err);
          isPlayingRef.current = false;
        });
    } else {
      // If already playing, pause it
      video.pause();
      isPlayingRef.current = false;
    }
  };
  
// Go to detail view
const handleGoDetail = () => {
    if (onGoDetail) {
      onGoDetail(videoPath, startTime);
    }
  };
  
  return (
    <div className="embedded-video-player">
      <div className="embedded-video-container">
        {isLoading && (
          <div className="embedded-video-loading">
            <div className="embedded-video-loading-spinner"></div>
            <span>Loading video...</span>
          </div>
        )}
        
        {error && (
          <div className="embedded-video-error">
            <span>{error}</span>
            <button className="embedded-video-close-button" onClick={onClose}>Close</button>
          </div>
        )}
        
        <video
          ref={videoRef}
          className="embedded-video"
          controls
          playsInline
          preload="auto"
          src={videoUrl}
        >
          Your browser does not support the video tag.
        </video>
      </div>
      
      <div className="embedded-video-controls">
        <div className="embedded-video-time-info">
          {displayTime} | Start: {formatTimeFromSeconds(adjustedStart)}
          {isClipMode ? ` | End: ${formatTimeFromSeconds(adjustedEnd)}` : ' (Free Mode)'}
        </div>

        <div className="embedded-video-buttons">
          <button 
            className="embedded-video-button embedded-video-play-button" 
            onClick={handlePlay}
          >
            Play
          </button>
          <button 
            className="embedded-video-button embedded-video-detail-button" 
            onClick={handleGoDetail}
          >
            Check Details
          </button>
          <button 
            className="embedded-video-button embedded-video-close-button" 
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default EnhancedVideoPlayer;